import React, { useState, useEffect } from "react";
import axios from "axios"; // Import Axios
import {
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Box,
  IconButton,
  BottomNavigation,
  BottomNavigationAction,
  TextField,
  Divider,
} from "@mui/material";
import {
  People as PeopleIcon,
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  AccountCircle as AccountCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Home as HomeIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import "react-toastify/dist/ReactToastify.css";

const baseUrl = process.env.REACT_APP_APP_URL;

const ApproverClientPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState(""); // Search query state
  const [filteredClients, setFilteredClients] = useState([]); // Filtered clients based on search
  const token = localStorage.getItem("CC_Token");
  const [isCavalierActive, setIsCavalierActive] = useState([]);

  const toggleCavalierStatus = async (user_id, isCavalier) => {
    const coach_id = getCurrentCoachId();
    try {
      const endpoint = isCavalier
        ? `${baseUrl}/api/users/removeCoach`
        : `${baseUrl}/api/users/assignCoach`;
  
      const response = await axios.patch(
        endpoint,
        { user_id, coach_id }, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.status === 200) {
        fetchClients(); // Met à jour la liste des cavaliers
        setIsCavalierActive((prevStatus) => !prevStatus);
      }
    } catch (error) {
      console.error("Erreur lors du changement de statut du cavalier :", error);
    }
  };
  const getCurrentCoachId = () => {
    const userString = localStorage.getItem("user");
    if (userString) {
        try {
            const userData = JSON.parse(userString);
         
            return parseInt(userData.id);
        } catch (error) {
            console.error("Error parsing user data:", error);
            return 0;
        }
    }
    return 0;
};
  // Fetch clients from the server
  const fetchClients = async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/users/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const clients = response.data.members;
      setClients(clients);
      setFilteredClients(clients); // Initialize with all clients
      const activeCavaliers = clients.filter(member => member.isActive === 1 && member.isCavalier === 1);
      console.log(activeCavaliers);
      setIsCavalierActive(activeCavaliers);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  // Call fetchClients inside useEffect
  useEffect(() => {
    fetchClients();
  }, [token]);

  // Handle search query change
  const handleSearchChange = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    // Filter clients based on the search query
    if (query) {
      const filtered = clients.filter((client) =>
        client.name.toLowerCase().includes(query)
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients); // Reset to all clients if search is cleared
    }
  };

  const handleApprove = async (id) => {
    try {
      const response = await axios.patch(
        `${baseUrl}/api/admin/activate-user/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        fetchClients(); // Reuse fetchClients to refresh the client list
      }
    } catch (error) {
      console.error("Error approving client:", error);
    }
  };

  const handleDisApprove = async (id) => {
    try {
      const response = await axios.patch(
        `${baseUrl}/api/admin/deactivate-user/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        fetchClients(); // Reuse fetchClients to refresh the client list
      }
    } catch (error) {
      console.error("Error approving client:", error);
    }
  };

  // Handle role assignment
  const handleAssignRole = async (id, role) => {
    handleApprove(id);
    try {
      const response = await axios.post(
        `${baseUrl}/api/admin/assign-role/${id}`,
        { role },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        fetchClients(); // Refresh client list after role assignment
      }
    } catch (error) {
      console.error("Error assigning role:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("CC_Token");
    localStorage.removeItem("user");
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    });
    setTimeout(() => navigate("/login"), 4000); // Redirect after toast
  };

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs");
  };

  const handleProfile = () => {
    navigate("/ProfileAdmin");
  };

  return (
    <div className="app">
      {/* Toast Container */}
      <ToastContainer />

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar
            alt="Logo"
            src={"../assets/cej.png"}
            sx={{ width: 40, height: 40, marginRight: "0.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container
        sx={{
          marginTop: "80px",
          marginBottom: "60px",
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            textAlign: "center",
            marginBottom: "1rem",
            color: "#003366",
            fontSize: { xs: "1.5rem", sm: "2rem" },
          }}
        >
          Liste des Clients
        </Typography>

        {/* Search Bar */}
        <TextField
          label="Rechercher par nom"
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={handleSearchChange}
          sx={{ marginBottom: "1rem" }}
        />

        {/* Non-Activated Clients Section */}
        <Typography
          variant="h5"
          sx={{
            marginTop: "2rem",
            marginBottom: "1rem",
            color: "#003366",
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          Comptes Non Activés
        </Typography>
        <Grid container spacing={3}>
          {filteredClients
            .filter((client) => client.isActive === 0)
            .map((client) => (
              <Grid item xs={12} sm={6} md={4} key={client.id}>
                <Card
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    padding: "1rem",
                    height: "90%",
                  }}
                >
                  <PeopleIcon
                    sx={{
                      fontSize: 40,
                      color: "#f44336",
                      marginBottom: "1rem",
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: "#003366" }}>
                      {client.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {client.email}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                      }}
                    >
                      Statut : Compte Non Actif
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: "space-between", width: "100%" }}>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ width: "100%", height: "70px" }} // Added height
                      onClick={() => handleApprove(client.id)}
                    >
                      Activer le compte
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{ width: "100%", height: "70px" }} // Added height
                      onClick={() => handleAssignRole(client.id, "admin")}
                    >
                      Définir comme Admin
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ width: "100%", height: "70px" }} // Added height
                      onClick={() => handleAssignRole(client.id, "instructor")}
                    >
                      Définir comme coach
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
        </Grid>

        <Divider sx={{ marginY: "3rem", backgroundColor: "#003366" }} />

        {/* Activated Clients Section */}
        <Typography
          variant="h5"
          sx={{
            marginBottom: "1rem",
            marginTop: "2rem",
            color: "#003366",
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          Comptes Activés
        </Typography>
        <Grid container spacing={3}>
          {filteredClients
            .filter((client) => client.isActive === 1)
            .map((client) => (
              <Grid item xs={12} sm={6} md={4} key={client.id}>
               <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "1rem",
        height: "90%",
      }}
    >
      <PeopleIcon
        sx={{
          fontSize: 40,
          color: "#4caf50",
          marginBottom: "1rem",
        }}
      />
      <CardActions>
        <Button
          variant="contained"
          color={client.isCavalier ? "error" : "success"}
          sx={{ width: "150px", marginTop: "1rem", height: "50px" }}
          onClick={() => toggleCavalierStatus(client.id, client.isCavalier)}
          >
          {client.isCavalier ? "Supprimer de la checklist" : "Ajouter à la checklist"}
        </Button>
      </CardActions>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" sx={{ color: "#003366" }}>
          {client.name}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {client.email}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: client.isCavalier ? "green" : "red" }}
        >
          Statut Cavalier : {client.isCavalier ? "Actif" : "Inactif"}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between", width: "100%" }}>
        <Button
          variant="contained"
          color="warning"
          sx={{ width: "100%", marginTop: "1rem", height: "70px" }}
          onClick={() => handleDisApprove(client.id)}
        >
          Désactiver le compte
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ width: "100%", marginTop: "1rem", height: "70px" }}
          onClick={() => handleAssignRole(client.id, "admin")}
        >
          Définir comme Admin
        </Button>
        <Button
          variant="contained"
          color="secondary"
          sx={{ width: "100%", marginTop: "1rem", height: "70px" }}
          onClick={() => handleAssignRole(client.id, "instructor")}
        >
          Définir comme coach
        </Button>
      </CardActions>
    </Card>
              </Grid>
            ))}
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/Admin")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminPaiment")}
          />
        </BottomNavigation>
      </Box>
    </div>
  );
};

export default ApproverClientPage;
